/* Phase 14A -- iOS Safari + push detection helpers.
 *
 * iOS Safari (as of iOS 16.4+) supports Web Push ONLY when the site is
 * installed as a PWA (Add to Home Screen). We detect that here and expose
 * a helper the shell uses to nudge iOS users through the A2HS ritual.
 */

export function isIos() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isIpad = /iPad|Macintosh/.test(ua) && "ontouchend" in document;
  return /iPhone|iPod/.test(ua) || isIpad;
}

export function isSafari() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /Safari\//.test(ua) && !/Chrome\/|CriOS|FxiOS|EdgiOS/.test(ua);
}

export function isStandalone() {
  if (typeof window === "undefined") return false;
  const displayMode = window.matchMedia && window.matchMedia("(display-mode: standalone)");
  return (displayMode && displayMode.matches) || window.navigator.standalone === true;
}

/* iOS Safari, not installed = candidate for the A2HS nudge. */
export function isIosSafariNotInstalled() {
  return isIos() && isSafari() && !isStandalone();
}

/* Local-first-seen tracker so we can wait 3 days before nagging. */
const FIRST_SEEN_KEY = "sharda_first_seen_at";
const DISMISSED_KEY  = "sharda_ios_a2hs_dismissed_at";

export function markFirstSeen() {
  if (!localStorage.getItem(FIRST_SEEN_KEY)) {
    localStorage.setItem(FIRST_SEEN_KEY, String(Date.now()));
  }
}

export function daysSinceFirstSeen() {
  const raw = localStorage.getItem(FIRST_SEEN_KEY);
  if (!raw) return 0;
  const ts = parseInt(raw, 10);
  if (!ts || Number.isNaN(ts)) return 0;
  return (Date.now() - ts) / (24 * 60 * 60 * 1000);
}

export function isIosPromptDismissed() {
  const raw = localStorage.getItem(DISMISSED_KEY);
  if (!raw) return false;
  const ts = parseInt(raw, 10);
  if (!ts || Number.isNaN(ts)) return false;
  // Dismissal lasts 30 days.
  return (Date.now() - ts) < 30 * 24 * 60 * 60 * 1000;
}

export function dismissIosPrompt() {
  localStorage.setItem(DISMISSED_KEY, String(Date.now()));
}

/* Combined trigger rule per spec:
 *   Show iOS A2HS prompt if:
 *     - iOS Safari, not standalone
 *     - AND (been 3+ days since first seen OR force=true e.g. push toggle)
 *     - AND not dismissed within 30 days
 */
export function shouldShowIosPrompt({ force = false } = {}) {
  if (!isIosSafariNotInstalled()) return false;
  if (isIosPromptDismissed()) return false;
  if (force) return true;
  return daysSinceFirstSeen() >= 3;
}
